import {
  onCLS,
  onINP,
  onLCP,
  onFCP,
  onTTFB,
  CLSThresholds,
} from 'web-vitals/attribution';
import type {
  CLSMetricWithAttribution,
  INPMetricWithAttribution,
} from 'web-vitals/attribution';
import type { MetricRating, MetricReport } from '../contracts/metricReport';
import type { RouteObserver } from '../routeObserver';
import {
  toClsReport,
  toInpReport,
  toLcpReport,
  toFcpReport,
  toTtfbReport,
} from './mappers';

export type PerViewReporter = (m: MetricReport, url: string) => void;

// web-vitals rates CLS against the page-cumulative value; the per-view accumulated sum needs to be re-rated against the same thresholds.
const clsRating = (value: number): MetricRating => {
  if (value <= CLSThresholds[0]) return 'good';
  if (value <= CLSThresholds[1]) return 'needs-improvement';
  return 'poor';
};

const setupPerViewMetrics = (
  report: PerViewReporter,
  routeObserver: Pick<RouteObserver, 'subscribe' | 'getUrl'>,
): void => {
  // LCP/FCP/TTFB describe the initial page load and are always attributed to it, even when they finalize (or flush) after a soft navigation.
  const initialUrl = routeObserver.getUrl();
  // The view currently accumulating CLS/INP. Flushes run before this is advanced, so each view's metrics carry the URL they were measured on.
  let viewUrl = initialUrl;
  // Start of the current view on the performance.now() timeline.
  let viewStartTime = 0;

  // --- CLS: accumulate deltas per view ---
  let clsAccumulated = 0;
  let clsLatest: CLSMetricWithAttribution | null = null;

  onCLS(
    metric => {
      clsAccumulated += metric.delta;
      clsLatest = metric;
    },
    { reportAllChanges: true },
  );

  const flushCls = () => {
    if (!clsLatest) return;
    report(
      {
        ...toClsReport(clsLatest),
        value: clsAccumulated,
        delta: clsAccumulated,
        rating: clsRating(clsAccumulated),
      },
      viewUrl,
    );
    clsAccumulated = 0;
    clsLatest = null;
  };

  // --- INP: high-water mark per view ---
  let inpHighWater: INPMetricWithAttribution | null = null;

  onINP(
    metric => {
      // INP is page-scoped in web-vitals: its value can change due to interactions from earlier views. Only count updates whose driving interaction happened during the current view.
      if (metric.attribution.interactionTime < viewStartTime) return;
      if (!inpHighWater || metric.value > inpHighWater.value) {
        inpHighWater = metric;
      }
    },
    { reportAllChanges: true },
  );

  const flushInp = () => {
    if (!inpHighWater) return;
    report(toInpReport(inpHighWater), viewUrl);
    inpHighWater = null;
  };

  // --- LCP / FCP / TTFB: pending slots, reported once each against the initial URL. No one-shot flag: LCP can finalize after an early programmatic navigation, in which case it flushes on the next route change or tab-hide instead of being dropped.
  let lcpPending: MetricReport | null = null;
  let fcpPending: MetricReport | null = null;
  let ttfbPending: MetricReport | null = null;

  onLCP(metric => {
    lcpPending = toLcpReport(metric);
  });

  onFCP(metric => {
    fcpPending = toFcpReport(metric);
  });

  onTTFB(metric => {
    ttfbPending = toTtfbReport(metric);
  });

  const flushInitialMetrics = () => {
    if (lcpPending) {
      report(lcpPending, initialUrl);
      lcpPending = null;
    }
    if (fcpPending) {
      report(fcpPending, initialUrl);
      fcpPending = null;
    }
    if (ttfbPending) {
      report(ttfbPending, initialUrl);
      ttfbPending = null;
    }
  };

  const flushView = () => {
    flushInitialMetrics();
    flushCls();
    flushInp();
  };

  routeObserver.subscribe(newUrl => {
    flushView();
    viewUrl = newUrl;
    viewStartTime = performance.now();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') return;
    flushView();
  });
};

export default setupPerViewMetrics;
